import React, { useEffect, useState } from "react";
import { omnichannelInboxApi } from "../../services/omnichannelInboxApi";

const formatContextTimestamp = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Africa/Harare",
  }).format(date);
};

const relationLabelFor = (item) => {
  const explicitLabel = String(item?.relation_label || "").trim();
  if (explicitLabel) return explicitLabel;

  switch (String(item?.relation_basis || "").trim()) {
    case "contact_id":
      return "Linked contact";
    case "merged_contact":
      return "Merged contact";
    case "fallback_phone":
      return "Exact phone match";
    case "fallback_email":
      return "Exact email match";
    default:
      return "Resolved history";
  }
};

export default function CustomerContextPanel({
  channelLabel,
  conversation,
  onOpenConversation,
}) {
  const contact = conversation?.contact;
  const history = conversation?.history || {};
  const recentInquiries = Array.isArray(history?.recent_inquiries) ? history.recent_inquiries : [];
  const relatedConversations = Array.isArray(history?.related_conversations) ? history.related_conversations : [];
  const workstream = conversation?.workstream;
  const lock = conversation?.lock;
  const rollups = conversation?.contact_rollups || null;

  // ── Hot Client flag ───────────────────────────────────────────────────────
  const contactId = contact?.id || conversation?.contact_id || null;
  const [isHotClient, setIsHotClient] = useState(Boolean(contact?.is_hot_client));
  const [hotClientNote, setHotClientNote] = useState(contact?.hot_client_note || "");
  const [hotClientEditing, setHotClientEditing] = useState(false);
  const [hotClientSaving, setHotClientSaving] = useState(false);

  // Sync when conversation/contact changes
  useEffect(() => {
    setIsHotClient(Boolean(contact?.is_hot_client));
    setHotClientNote(contact?.hot_client_note || "");
    setHotClientEditing(false);
  }, [contact?.id, contact?.is_hot_client, contact?.hot_client_note]);

  const handleHotClientToggle = async (newValue) => {
    if (!contactId) return;
    setHotClientSaving(true);
    try {
      await omnichannelInboxApi.setHotClientStatus(contactId, {
        is_hot_client: newValue,
        hot_client_note: newValue ? hotClientNote : null,
      });
      setIsHotClient(newValue);
      if (!newValue) { setHotClientNote(""); setHotClientEditing(false); }
    } catch (err) {
      // silently revert — no toast dependency here
      setIsHotClient(!newValue);
    } finally {
      setHotClientSaving(false);
    }
  };

  const handleHotClientNoteSave = async () => {
    if (!contactId) return;
    setHotClientSaving(true);
    try {
      await omnichannelInboxApi.setHotClientStatus(contactId, {
        is_hot_client: true,
        hot_client_note: hotClientNote,
      });
      setHotClientEditing(false);
    } finally {
      setHotClientSaving(false);
    }
  };

  // ── Omnichannel Customer Timeline ─────────────────────────────────────────
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timelineItems, setTimelineItems] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState("");

  useEffect(() => {
    if (!timelineOpen || !contactId) return;
    let cancelled = false;
    setTimelineLoading(true);
    setTimelineError("");
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Timeline request timed out. Please try again.")), 60000)
    );
    Promise.race([omnichannelInboxApi.getContactTimeline(contactId), timeoutPromise])
      .then((res) => {
        if (cancelled) return;
        setTimelineItems(Array.isArray(res?.data) ? res.data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setTimelineError(err?.response?.data?.message || err?.message || "Failed to load timeline.");
      })
      .finally(() => { if (!cancelled) setTimelineLoading(false); });
    return () => { cancelled = true; };
  }, [timelineOpen, contactId]);
  const SL = { fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", color: "#bbbbbb", marginBottom: 6, fontFamily: "'Inter',system-ui,sans-serif" };
  const CARD = { background: "#fff", border: "1px solid #efefef", borderRadius: 10, padding: 12 };
  const inits = (name) => (name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();
  const openConversationInInbox = (conversationId, channel = "") => {
    if (!conversationId) return;
    onOpenConversation?.(conversationId, channel);
  };
  const openInquiryConversation = (inquiry) => {
    const linkedConversation = inquiry?.linked_conversation || null;
    if (!linkedConversation?.id) return;
    openConversationInInbox(linkedConversation.id, linkedConversation.channel);
  };
  const historyHasContent = recentInquiries.length > 0 || relatedConversations.length > 0;

  return (
    <div style={{ fontFamily: "'Inter',system-ui,sans-serif", display: "flex", flexDirection: "column", gap: 12, padding: "0 0 14px" }}>

      {/* ── 🔥 Hot Client Toggle ── */}
      {contactId && (
        <div style={{ padding: "0 16px" }}>
          <div
            style={{
              borderRadius: 12,
              overflow: "hidden",
              border: isHotClient ? "1.5px solid #f97316" : "1.5px solid #e5e7eb",
              background: isHotClient ? "linear-gradient(135deg,#fff7ed,#ffedd5)" : "#fafafa",
              transition: "all 200ms",
            }}
          >
            {/* Header row */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 20, lineHeight: 1 }}>{isHotClient ? "🔥" : "☆"}</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: isHotClient ? "#c2410c" : "#6b7280" }}>
                    {isHotClient ? "HOT CLIENT — Priority" : "Standard client"}
                  </div>
                  <div style={{ fontSize: 10, color: isHotClient ? "#ea580c" : "#9ca3af", marginTop: 1 }}>
                    {isHotClient
                      ? "WhatsApp alert fires when they email in"
                      : "Mark as hot to get instant WhatsApp alerts"}
                  </div>
                </div>
              </div>
              {/* Toggle switch */}
              <button
                disabled={hotClientSaving}
                onClick={() => handleHotClientToggle(!isHotClient)}
                style={{
                  width: 42, height: 24, borderRadius: 12, border: "none", cursor: hotClientSaving ? "wait" : "pointer",
                  background: isHotClient ? "#f97316" : "#d1d5db",
                  position: "relative", transition: "background 200ms", flexShrink: 0,
                  outline: "none",
                }}
              >
                <span style={{
                  position: "absolute", top: 3, left: isHotClient ? 21 : 3,
                  width: 18, height: 18, borderRadius: "50%", background: "#fff",
                  transition: "left 200ms", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                }} />
              </button>
            </div>

            {/* Note section — only when hot */}
            {isHotClient && (
              <div style={{ borderTop: "1px solid #fed7aa", padding: "8px 14px 10px" }}>
                {hotClientEditing ? (
                  <div>
                    <textarea
                      autoFocus
                      value={hotClientNote}
                      onChange={e => setHotClientNote(e.target.value)}
                      placeholder="Add a note about this client (e.g. 'Diamond VIP — always books Business Class')"
                      rows={3}
                      style={{
                        width: "100%", boxSizing: "border-box", resize: "none",
                        fontSize: 11, color: "#374151", padding: "6px 8px",
                        border: "1px solid #fdba74", borderRadius: 6,
                        outline: "none", fontFamily: "inherit",
                        background: "#fff",
                      }}
                    />
                    <div style={{ display: "flex", gap: 6, marginTop: 5, justifyContent: "flex-end" }}>
                      <button
                        onClick={() => { setHotClientEditing(false); setHotClientNote(contact?.hot_client_note || ""); }}
                        style={{ fontSize: 11, padding: "3px 10px", borderRadius: 5, border: "1px solid #d1d5db", background: "#fff", cursor: "pointer", color: "#6b7280" }}
                      >Cancel</button>
                      <button
                        onClick={handleHotClientNoteSave}
                        disabled={hotClientSaving}
                        style={{ fontSize: 11, padding: "3px 12px", borderRadius: 5, border: "none", background: "#f97316", color: "#fff", cursor: hotClientSaving ? "wait" : "pointer", fontWeight: 600 }}
                      >{hotClientSaving ? "Saving…" : "Save note"}</button>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => setHotClientEditing(true)}
                    style={{ cursor: "pointer" }}
                    title="Click to edit note"
                  >
                    <div style={{ fontSize: 10, fontWeight: 600, color: "#ea580c", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 3 }}>
                      Priority note
                    </div>
                    <div style={{ fontSize: 11, color: hotClientNote ? "#374151" : "#9ca3af", fontStyle: hotClientNote ? "normal" : "italic", lineHeight: 1.45 }}>
                      {hotClientNote || "Tap to add a note about this client…"}
                    </div>
                    <div style={{ fontSize: 10, color: "#f97316", marginTop: 4 }}>✏️ Edit note</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Section 6: Contact & Inquiry ── */}
      <div style={{ padding: "0 16px" }}>
        <div style={SL}>Contact &amp; inquiry</div>
        <div style={CARD}>
          {/* Mini contact header */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, paddingBottom: 10, borderBottom: "1px solid #f0f0f0" }}>
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: "#dcfce7", color: "#166534", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {inits(conversation?.customer?.name || contact?.name)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#111111" }}>{conversation?.customer?.name || contact?.name || "Customer"}</div>
              <div style={{ fontSize: 11, color: "#aaaaaa", marginTop: 1 }}>Primary {channelLabel} contact</div>
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 4, flexShrink: 0 }}>
              <span style={{ fontSize: 10, fontWeight: 500, padding: "2px 7px", borderRadius: 20, background: "#e0f2fe", color: "#0369a1" }}>{(channelLabel || "").toLowerCase()}</span>
              {contact?.status && <span style={{ fontSize: 10, fontWeight: 500, padding: "2px 7px", borderRadius: 20, background: "#dcfce7", color: "#166534" }}>{contact.status}</span>}
            </div>
          </div>
          {/* Data rows */}
          {(() => {
            const email = conversation?.customer?.email || contact?.email || null;
            const phone = conversation?.customer?.phone || contact?.phone || null;

            // Normalise phone for wa.me — strip all non-digit chars
            const waPhone = phone ? phone.replace(/\D/g, "") : null;

            const rowStyle = (last) => ({
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "5px 0", borderBottom: last ? "none" : "1px solid #f5f5f5",
            });

            return (
              <>
                {/* Email */}
                <div style={rowStyle(false)}>
                  <span style={{ fontSize: 11, fontWeight: 500, color: "#aaaaaa" }}>Email</span>
                  {email
                    ? <a href={`mailto:${email}`} style={{ fontSize: 11, fontWeight: 500, color: "#aaaaaa", textDecoration: "none" }} title="Send email">{email}</a>
                    : <span style={{ fontSize: 11, color: "#aaaaaa" }}>-</span>
                  }
                </div>

                {/* Phone + tel link + WhatsApp button */}
                <div style={rowStyle(false)}>
                  <span style={{ fontSize: 11, fontWeight: 500, color: "#aaaaaa" }}>Phone</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {phone
                      ? (
                        <a
                          href={`tel:${phone}`}
                          style={{ fontSize: 12, fontWeight: 500, color: "#111111", textDecoration: "none" }}
                          title="Call this number"
                        >
                          {phone}
                        </a>
                      )
                      : <span style={{ fontSize: 12, color: "#aaaaaa" }}>-</span>
                    }
                    {waPhone && (
                      <a
                        href={`https://wa.me/${waPhone}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open WhatsApp chat"
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 3,
                          fontSize: 10, fontWeight: 600, color: "#ffffff",
                          background: "#25D366", borderRadius: 6,
                          padding: "2px 7px", textDecoration: "none",
                          lineHeight: 1.5, flexShrink: 0,
                        }}
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                          <path d="M12 0C5.374 0 0 5.373 0 12c0 2.117.549 4.107 1.508 5.84L.057 23.17a.75.75 0 0 0 .92.92l5.34-1.453A11.945 11.945 0 0 0 12 24c6.626 0 12-5.374 12-12S18.626 0 12 0zm0 21.818a9.818 9.818 0 0 1-5.006-1.372l-.36-.214-3.716 1.01 1.012-3.696-.235-.381A9.818 9.818 0 1 1 12 21.818z"/>
                        </svg>
                        WhatsApp
                      </a>
                    )}
                  </div>
                </div>

                {/* Workstream */}
                <div style={rowStyle(false)}>
                  <span style={{ fontSize: 11, fontWeight: 500, color: "#aaaaaa" }}>Workstream</span>
                  <span style={{ fontSize: 12, fontWeight: 500, color: "#111111" }}>{workstream?.name || "Unified Inbox"}</span>
                </div>

                {/* Lock */}
                <div style={rowStyle(true)}>
                  <span style={{ fontSize: 11, fontWeight: 500, color: "#aaaaaa" }}>Lock</span>
                  <span style={{ fontSize: 11, fontWeight: 500, color: "#aaaaaa" }}>
                    {lock?.locked_by?.name ? `Held by ${lock.locked_by.name}` : "Not locked"}
                  </span>
                </div>
              </>
            );
          })()}
        </div>
      </div>

      {/* ── Section 7: Customer Overview ── */}
      {rollups && (
        <div style={{ padding: "0 16px" }}>
          <div style={SL}>Customer overview</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7 }}>
            {[
              { label: "TOTAL INQUIRIES",  value: rollups?.total_inquiries ?? 0 },
              { label: "CONVERSATIONS",    value: rollups?.total_conversations ?? 0 },
              { label: "QUOTE REQUESTS",   value: rollups?.quote_requests_total ?? 0 },
              { label: "LATEST CHANNEL",   value: rollups?.latest_channel ? String(rollups.latest_channel).toUpperCase() : "-", green: true },
            ].map(({ label, value, green }) => (
              <div key={label} style={{ background: "#f9f9f9", borderRadius: 8, padding: "9px 11px" }}>
                <div style={{ fontSize: 10, color: "#aaaaaa", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: green ? 12 : 14, fontWeight: 600, color: green ? "#16a34a" : "#111111" }}>{value}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 8, background: "#f9f9f9", borderRadius: 8, padding: "9px 11px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            <div>
              <div style={{ fontSize: 10, color: "#aaaaaa", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 2 }}>FIRST SEEN</div>
              <div style={{ fontSize: 11, fontWeight: 500, color: "#111111", marginTop: 2 }}>{formatContextTimestamp(rollups?.first_seen_at)}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, color: "#aaaaaa", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 2 }}>LAST SEEN</div>
              <div style={{ fontSize: 11, fontWeight: 500, color: "#111111", marginTop: 2 }}>{formatContextTimestamp(rollups?.last_seen_at)}</div>
            </div>
          </div>
        </div>
      )}

      {/* ── Section 8: Customer History ── */}
      {historyHasContent && (
        <div style={{ padding: "0 16px" }}>
          <div style={SL}>Customer history</div>
          <div style={CARD}>
            {recentInquiries.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#555555", marginBottom: 8 }}>Recent inquiries</div>
                {recentInquiries.slice(0, 5).map((inq, i) => {
              const assigneeName = inq.assigned_to
                ? (typeof inq.assigned_to === "object"
                  ? (inq.assigned_to.name || inq.assigned_to.email || "Assigned")
                  : String(inq.assigned_to))
                : null;
              const relationLabel = relationLabelFor(inq);
              const inquiryName = inq.full_name || inq.name || inq.customer_name || "Unknown";
              const linkedConversation = inq?.linked_conversation || null;
              const canOpenLinkedConversation = Boolean(linkedConversation?.id);
              const borderBottom = i < Math.min(recentInquiries.length, 5) - 1 || relatedConversations.length > 0;
              const openLinkedConversation = () => {
                if (!canOpenLinkedConversation) return;
                openInquiryConversation(inq);
              };
              return (
                <div
                  key={inq?.id || i}
                  onClick={openLinkedConversation}
                  onKeyDown={(event) => {
                    if (!canOpenLinkedConversation) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      openLinkedConversation();
                    }
                  }}
                  onMouseEnter={(event) => {
                    if (!canOpenLinkedConversation) return;
                    event.currentTarget.style.background = "#f0fdf4";
                    event.currentTarget.style.borderRadius = "6px";
                  }}
                  onMouseLeave={(event) => {
                    if (!canOpenLinkedConversation) return;
                    event.currentTarget.style.background = "transparent";
                    event.currentTarget.style.borderRadius = "0";
                  }}
                  role={canOpenLinkedConversation ? "button" : undefined}
                  tabIndex={canOpenLinkedConversation ? 0 : -1}
                  title={canOpenLinkedConversation ? "Open linked chat in Unified Inbox" : "No linked chat is available for this inquiry."}
                  style={{
                    padding: "8px 6px",
                    borderBottom: borderBottom ? "1px solid #f5f5f5" : "none",
                    cursor: canOpenLinkedConversation ? "pointer" : "default",
                    transition: "background 120ms",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#111111" }}>#{inq.id} - {inquiryName}</span>
                    <span style={{ fontSize: 10, fontWeight: 500, color: canOpenLinkedConversation ? "#0369a1" : "#6b7280", display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                      <span>{relationLabel}</span>
                      {canOpenLinkedConversation ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                          <span>Open chat</span>
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7 }}>
                            <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
                          </svg>
                        </span>
                      ) : (
                        <span style={{ padding: "2px 6px", borderRadius: 999, background: "#f3f4f6", color: "#6b7280" }}>
                          No linked chat
                        </span>
                      )}
                    </span>
                  </div>
                  <div style={{ fontSize: 10, color: canOpenLinkedConversation ? "#16a34a" : "#9ca3af", marginTop: 3 }}>
                    {canOpenLinkedConversation
                      ? `Linked ${String(linkedConversation.channel || "conversation").toUpperCase()} conversation opens in Unified Inbox`
                      : "This inquiry has no verified live chat or linked conversation in Unified Inbox."}
                  </div>
                  <div style={{ fontSize: 11, color: "#aaaaaa", marginTop: 1 }}>
                    {[inq.status, assigneeName ? `Assigned: ${assigneeName}` : "Unassigned"].filter(Boolean).join(" · ")}
                  </div>
                </div>
              );
                })}
              </>
            )}
            {relatedConversations.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#555555", marginTop: recentInquiries.length > 0 ? 10 : 0, marginBottom: 8 }}>
                  Linked chats
                </div>
                {relatedConversations.slice(0, 5).map((item, i) => {
                  const relationLabel = relationLabelFor(item);
                  const canOpenConversation = Boolean(item?.id);
                  const openRelatedConversation = () => {
                    if (!canOpenConversation) return;
                    openConversationInInbox(item.id, item.channel);
                  };

                  return (
                    <div
                      key={item?.id || `related-${i}`}
                      onClick={openRelatedConversation}
                      onKeyDown={(event) => {
                        if (!canOpenConversation) return;
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openRelatedConversation();
                        }
                      }}
                      onMouseEnter={(event) => {
                        if (!canOpenConversation) return;
                        event.currentTarget.style.background = "#f0fdf4";
                        event.currentTarget.style.borderRadius = "6px";
                      }}
                      onMouseLeave={(event) => {
                        if (!canOpenConversation) return;
                        event.currentTarget.style.background = "transparent";
                        event.currentTarget.style.borderRadius = "0";
                      }}
                      role={canOpenConversation ? "button" : undefined}
                      tabIndex={canOpenConversation ? 0 : -1}
                      title={canOpenConversation ? "Open linked chat in Unified Inbox" : undefined}
                      style={{
                        padding: "8px 6px",
                        borderBottom: i < Math.min(relatedConversations.length, 5) - 1 ? "1px solid #f5f5f5" : "none",
                        cursor: canOpenConversation ? "pointer" : "default",
                        transition: "background 120ms",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: "#111111" }}>
                          #{item.id} - {item.subject || item.last_message_preview || "Conversation"}
                        </span>
                        <span style={{ fontSize: 10, fontWeight: 500, color: "#0369a1", display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                          <span>{relationLabel}</span>
                          <span style={{ padding: "2px 6px", borderRadius: 999, background: "#ecfeff", color: "#0f766e" }}>
                            {String(item.channel || "chat").toUpperCase()}
                          </span>
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: "#aaaaaa", marginTop: 1 }}>
                        {[item.status, "Open in Unified Inbox"].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Omnichannel Customer Timeline ── */}
      {contactId && (
        <div style={{ padding: "0 16px 14px", fontFamily: "'Inter', system-ui, sans-serif" }}>
          <div style={SL}>Omnichannel customer timeline</div>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
            {/* Header */}
            <div
              onClick={() => setTimelineOpen((p) => !p)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "9px 12px", background: "#f9fafb", cursor: "pointer", userSelect: "none",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, fontWeight: 600, color: "#163126" }}>
                <span style={{ fontSize: 14 }}>🕐</span>
                All Conversations
                {timelineItems.length > 0 && (
                  <span style={{ background: "#dcfce7", color: "#166534", borderRadius: 10, padding: "1px 7px", fontSize: 9, fontWeight: 700 }}>
                    {timelineItems.length}
                  </span>
                )}
              </div>
              <span style={{ color: "#9ca3af", fontSize: 13 }}>{timelineOpen ? "▲" : "▼"}</span>
            </div>

            {timelineOpen && (
              <div>
                {timelineLoading && (
                  <div style={{ padding: "16px", textAlign: "center", fontSize: 12, color: "#9ca3af" }}>
                    Loading timeline…
                  </div>
                )}
                {timelineError && (
                  <div style={{ padding: "10px 12px", fontSize: 11, color: "#dc2626", background: "#fef2f2" }}>
                    {timelineError}
                  </div>
                )}
                {!timelineLoading && !timelineError && timelineItems.length === 0 && (
                  <div style={{ padding: "14px 12px", fontSize: 11, color: "#9ca3af", textAlign: "center", fontStyle: "italic" }}>
                    No previous conversations found.
                  </div>
                )}
                {!timelineLoading && timelineItems.map((item, i) => {
                  const CHANNEL_COLORS = { whatsapp: "#22c55e", email: "#3b82f6", voice: "#8b5cf6" };
                  const STATUS_COLORS = { open: "#2563eb", resolved: "#16a34a", closed: "#6b7280", pending: "#d97706", waiting_customer: "#ea580c" };
                  const channelColor = CHANNEL_COLORS[item.channel] || "#9ca3af";
                  const statusColor = STATUS_COLORS[item.status] || "#6b7280";
                  const isCurrentConv = String(item.id) === String(conversation?.id);
                  const canOpenConversation = Boolean(item?.id) && !isCurrentConv;
                  const dateStr = item.last_activity_at
                    ? new Date(item.last_activity_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
                    : "";
                  const openTimelineConversation = () => {
                    if (!canOpenConversation) return;
                    openConversationInInbox(item.id, item.channel);
                  };

                  return (
                    <div
                      key={item.id}
                      onClick={openTimelineConversation}
                      onKeyDown={(event) => {
                        if (!canOpenConversation) return;
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openTimelineConversation();
                        }
                      }}
                      onMouseEnter={(event) => {
                        if (!canOpenConversation) return;
                        event.currentTarget.style.background = "#f8fffb";
                      }}
                      onMouseLeave={(event) => {
                        if (!canOpenConversation) return;
                        event.currentTarget.style.background = isCurrentConv ? "#f0fdf4" : "#ffffff";
                      }}
                      role={canOpenConversation ? "button" : undefined}
                      tabIndex={canOpenConversation ? 0 : -1}
                      title={canOpenConversation ? "Open this conversation in Unified Inbox" : undefined}
                      style={{
                        padding: "9px 12px",
                        borderTop: i === 0 ? "1px solid #f3f4f6" : "1px solid #f9fafb",
                        background: isCurrentConv ? "#f0fdf4" : "#ffffff",
                        position: "relative",
                        cursor: canOpenConversation ? "pointer" : "default",
                        transition: "background 120ms",
                      }}
                    >
                      {/* Channel + Date row */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <span style={{
                            fontSize: 9, fontWeight: 700, textTransform: "uppercase",
                            color: channelColor, background: `${channelColor}18`,
                            padding: "2px 6px", borderRadius: 20,
                          }}>
                            {item.channel || "—"}
                          </span>
                          <span style={{
                            fontSize: 9, fontWeight: 600, textTransform: "uppercase",
                            color: statusColor, background: `${statusColor}18`,
                            padding: "2px 6px", borderRadius: 20,
                          }}>
                            {item.status || "—"}
                          </span>
                          {isCurrentConv && (
                            <span style={{ fontSize: 9, fontWeight: 700, color: "#16a34a", background: "#dcfce7", padding: "2px 6px", borderRadius: 20 }}>
                              current
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: 10, color: "#9ca3af" }}>{dateStr}</span>
                      </div>

                      {/* Subject / preview */}
                      <div style={{
                        fontSize: 11, fontWeight: 500, color: "#374151",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        marginBottom: item.assigned_to_name ? 3 : 0,
                      }}>
                        {item.subject || item.last_message_preview || "No subject"}
                      </div>

                      {/* Assigned to */}
                      {item.assigned_to_name && (
                        <div style={{ fontSize: 10, color: "#9ca3af" }}>
                          Assigned: {item.assigned_to_name}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
